// Zustand store for ephemeral UI state — selection in bulk mode, toast queue,
// open modal, sidebar collapse on tablet. Persistent state (designs,
// folders, brand kit) lives in TanStack Query cache, not here.

import { create } from 'zustand';

export type ToastType = 'default' | 'success' | 'warn' | 'error';

export interface Toast {
  id: number;
  type: ToastType;
  message: string;
  action?: { label: string; onClick: () => void };
}

export type ViewName =
  | 'home'
  | 'reorder'
  | 'designs'
  | 'orders'
  | 'kit'
  | 'team'
  | 'help';

interface StudioStoreState {
  // ── View routing ────────────────────────────────────
  view: ViewName;
  setView: (v: ViewName) => void;

  // ── Selection in My Designs bulk mode ──────────────
  bulkMode: boolean;
  selected: Set<string>;
  toggleSelect: (id: string) => void;
  enterBulk: () => void;
  exitBulk: () => void;
  clearSelected: () => void;

  // ── Toasts ──────────────────────────────────────────
  toasts: Toast[];
  pushToast: (t: Omit<Toast, 'id'>) => void;
  dismissToast: (id: number) => void;

  // ── Detail panel ────────────────────────────────────
  detailDesignId: string | null;
  openDetail: (id: string) => void;
  closeDetail: () => void;
}

let nextToastId = 1;

export const useStudio = create<StudioStoreState>((set) => ({
  view: 'home',
  // Ship 5e: when the user navigates to a different tab, auto-close any
  // open detail drawer. Without this, the drawer would stay overlaid on
  // top of whichever view they jumped to, which feels broken — they
  // expected the click to take them somewhere new, and the drawer's
  // half-pinned state on top is confusing. Closing it on every nav is
  // the right call: if they want it open, they re-click the card.
  setView: (v) => set({ view: v, detailDesignId: null }),

  bulkMode: false,
  selected: new Set<string>(),
  toggleSelect: (id) =>
    set((s) => {
      const next = new Set(s.selected);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return { selected: next, bulkMode: next.size > 0 };
    }),
  enterBulk: () => set({ bulkMode: true }),
  exitBulk: () => set({ bulkMode: false, selected: new Set() }),
  clearSelected: () => set({ selected: new Set(), bulkMode: false }),

  toasts: [],
  pushToast: (t) =>
    set((s) => ({ toasts: [...s.toasts, { ...t, id: nextToastId++ }] })),
  dismissToast: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),

  detailDesignId: null,
  openDetail: (id) => set({ detailDesignId: id }),
  closeDetail: () => set({ detailDesignId: null }),
}));
